<template>
  <page-outline>
    <template slot="pageContent">
      <div :class="['content bg-light',{ expanded: !isSidebarOpen }]">
        <div>
          <b-container fluid>
            <!-- TITLE -->
            <div class="content-title">
              <b-row>
                <h1>Daily Reconciliation Report</h1>
              </b-row>
              <b-row>
                <span>Comprehensive daily reporting for compliance and audit purposes</span>
              </b-row>
            </div>
          </b-container>

          <div class="card p-6">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div style="display: flex; gap: 1rem; align-items: center;">
                <i style="font-size: 1.5rem;" class="fa-light fa-calendar"></i>
                <span style="font-weight: bold;">Report Date:</span>
                <datetime-picker class="z-1" format="DD/MM/YYYY"
                  :class="{'filter-item-input': true, 'filter-item-input-complete': dateBegin, 'error-datepicker': dateBeginError}"
                  customInputParentClass="filter-item-content"
                  :maxLength="19"
                  :placeholderValue="$t('common.datetime_picker_empty_date')"
                  inputIcon="fa-solid fa-calendar"
                  v-model="dateBegin"
                  @input="selectBeginDate">
                </datetime-picker>
                <span v-if="dateBeginError" class="text-danger">{{$t('errors.Data_format_invalid')}}</span>
                <button class="btn btn-outline-light" @click="refresh(new Date().toISOString())">Today</button>
                <button class="btn btn-outline-light" @click="refresh()">Refresh</button>
              </div>
              <button :class="['btn btn-validate',{disabled: totalResults == 0}]" @click="() => totalResults > 0 ? exportFiltered() :''" ><i class="fa-light fa-arrow-down-to-bracket"></i>Generate Daily Report</button>
            </div>
          </div>
  
          <b-container fluid>
            <!-- data -->
            <b-row class="stats">
              <b-col @click="stat.status != null ? selectStatus(stat.status): ''" v-for="(stat, index) in stats" v-bind:key="index">
                <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                  <div style="display: grid; row-gap: 0.5rem;">
                    <span style="text-wrap: nowrap;">{{ stat.title }}</span>
                    <span style="font-weight: bold;">{{ stat.description }}</span>
                    <span>{{ stat.description2 }}</span>
                  </div>
                  <i :style="{color: stat.color, 'background-color': stat.background, display: 'flex', 'justify-content': 'center', 'align-items': 'center', 'font-size': '1.5rem', height: '3rem', width: '3rem', 'border-radius': '0.625rem'}" :class="['fa-thin', stat.symbol]"></i>
                </div>
              </b-col>
            </b-row>
          </b-container>

          <!-- VIBANs list -->
          <div class="card p-6">
            <b-row style="display:flex; align-items:center; justify-content:space-between; padding: 0rem 1rem;">
              <div style="display: flex; gap: 1rem; align-items: center;">
                <span v-if="totalResults > 0">Showing {{totalResults}} {{ totalResults > 1 ? 'results' :'result' }}
                  </span>
                  <div style="display:flex; align-items:center; gap: 1rem;">
                    <span class="selected" v-if="selectedItems.length > 0">{{ selectedItems.length }} selected</span>
                    <button @click="clearSelection()" class="clear" v-if="selectedItems.length > 0"><i class="fa-classic fa-xmark"></i>Clear</button>
                  </div>
              </div>

              <div style="display:flex; align-items:center; gap: 1rem;">
                <button @click="exportSelected()" v-if="selectedItems.length > 0"  class="btn-outline btn-tertiary"><i class="fa-classic fa-arrow-down-to-line"></i>Export Selected ({{ selectedItems.length }})</button>
                
                <button @click="exportFiltered()" class="btn-outline button-primary"><i class="fa-classic fa-arrow-down-to-line"></i>Export Filtered</button>
  
                <button @click="exportAll()" class="btn-outline button-primary"><i class="fa-classic fa-arrow-down-to-line"></i>Export All</button>
              </div>
            </b-row>
            <hr>
            <div v-if="items != null">
              <template v-if="totalResults > 0">
                <b-table responsive
                  style="margin-bottom:0;"
                  :items="items"
                  :fields="fields"
                  primary-key="id"
                >
                  <template #head(checkbox)>
                    <label class="custom-checkbox">
                      <input type="checkbox" 
                        id="all" 
                        name="checkAll"
                        @change="selectAll($event)" />
                      <span class="checkmark"></span>
                    </label>
                  </template>
  
                  <template #cell(checkbox)="data">
                    <label class="custom-checkbox">
                      <input type="checkbox"
                            :value="data.item"
                            @change="toggleSelection($event, data.item)" />
                      <span class="checkmark"></span>
                    </label>
                  </template>
  
                  <template #cell(time)="data">
                    <span v-if="data.item.CreationDate">{{ data.item.CreationDate.split('T')[1] }}</span>
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(orderreference)="data">
                    <span v-if="data.item.Invoices[0] && data.item.Invoices[0].InvoiceIdentifier">{{ data.item.Invoices[0].InvoiceIdentifier }}</span>
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(paymentreference)="data">
                    <span v-if="data.item.Reference1">{{ data.item.Reference1 }}</span>
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(transactionType)="data">
                    <span v-if="data.item.Type == 1 && data.item.Direction == 0" class="btn btn-empty" style="display: flex; gap: 0.5rem; align-items: center; justify-content: center; cursor: default; color: #008236; background-color: #f0fdf4; border-color: #b9f8cf;"
                    >
                      <i class="fa-light fa-credit-card"></i>
                      Payment
                    </span>
  
                    <span v-else-if="data.item.Type == 0 && data.item.Direction == 1" class="btn btn-empty" style="display: flex; gap: 0.5rem; align-items: center; justify-content: center; cursor: default; color: #1d4ed8; background-color: #eff6ff; border-color: #bfdbfe;"
                    >
                      <i class="fa-light fa-money-bill"></i>
                      Order
                    </span>
  
                    <span v-else-if=" data.item.Type == 11 || data.item.Type == 3" class="btn btn-empty" style="display: flex; gap: 0.5rem; align-items: center; justify-content: center; cursor: default; color: #a16207; background-color: #fefce8; border-color: #fef08a;"
                    >
                      <i class="fa-light fa-money-bill-transfer"></i>
                      {{ data.item.Type == 11 ? 'Refund' : data.item.Type == 3 ? 'Credit Note' : '' }}
                    </span>
  
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(viban)="data">
                    <span v-if="data.item.VIBAN !== ''">{{ data.item.VIBAN }}</span>
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(amount)="data">
                    {{ data.item.Amount + '€' }}
                  </template>
  
                  <template #cell(type)="data">
                    <button v-if="data.item.ReconciliationMode == 1" style="cursor: default; gap: 0.5rem; display: flex; align-items: center; color: #fff; background-color: #155dfc; border-color: #155dfc;" class="btn-tertiary-full">Auto</button>
                    <button v-else-if="data.item.ReconciliationMode == 2" style="cursor: default; gap: 0.5rem; display: flex; align-items: center; color: #364153; background-color: #fbf9fa; border-color: #ebe6e7;" class="btn-tertiary-full">Manual</button>
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(reconciledBy)="data">
                    <span v-if="data.item.ReconciliationMode == 1">System</span>
                    <span v-else-if="data.item.ReconciliationMode == 2">User</span>
                    <span v-else>—</span>
                  </template>
  
                  <template #cell(status)="data">
                    <button  v-if="Number(data.item.Status) == 2" style="cursor: default; gap: 0.5rem; display: flex; align-items: center; color:#a16207; background-color: #fefce8; border-color: #fef08a;" class="btn-tertiary-full"><i class="fa-light fa-clock"></i>Pending</button>
  
                    <button  v-else-if="Number(data.item.Status) == 3" class="btn-tertiary-full" style="cursor: default; gap: 0.5rem; display: flex; align-items: center;color: #008236; background-color: #f0fdf4; border-color: #b9f8cf;"><i class="fa-light fa-circle-check"></i>Reconciled</button>
  
                    <button  v-else-if="Number(data.item.Status) == 1" class="btn-tertiary-full" style="cursor: default;  gap: 0.5rem; display: flex; align-items: center; color: #c10007; background-color: #fef2f2; border-color: #c1000773;"><i class="fa-light fa-triangle-exclamation"></i>Not Reconciled</button>
  
                    <button v-else  class="btn-tertiary-full" style="cursor: default;  gap: 0.5rem; display: flex; align-items: center; color:#364153; background-color:#fbf9fa; border-color: #ebe6e7;">Active</button>
                  </template>
                  
                </b-table>
              </template>
              <b-row v-else>
                <span>No reconciliations found for {{ this.dateBegin.replaceAll('/', '.') }}</span> 
              </b-row>
            </div>
            <div v-else style="display: flex; justify-content: center;" class="empty">
              <b-spinner label="Loading..."></b-spinner>
            </div>
          </div>

          <div class="card p-6" style="display: flex; justify-content: space-between; align-items: center; background-color: #f8f9fa !important;">
            <div style="display: flex; gap: 2rem; color: #4a5565;">
              <span>
                Report generated: {{ this.dateNow }}
              </span>
              <span>•</span>
              <span>
                Period: {{ this.dateBegin }}
              </span>
            </div>
            <span style="color: #4a5565; display: flex; gap: 1rem;"><i style="color: #00a63e;" class="fa-light fa-circle-check"></i>Audit-ready format</span>
          </div>
          
        </div>
      </div>
    </template>
  </page-outline>
</template>



<script>
import PageOutline from '@/components/common/PageOutline.vue'
import moment from 'moment'
import DateTimePicker from '@/components/common/dateTimePicker.vue';
import { refresh } from 'less';

export default {
  name: 'DailyReport',
  components: {
    'page-outline': PageOutline,
    'datetime-picker': DateTimePicker
  },
  data(){
    return {
      items: null,
      fields: [
        { key: 'checkbox', thClass: 'text-center align-middle  border-top border-bottom border-left bg-light border-right-0', tdClass: 'text-center align-middle border-left border-bottom'},
        { key: 'time', label: 'Time', thClass: 'text-left align-middle border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-left border-bottom'},
        { key: 'orderReference', label: 'Order Reference', thClass: 'text-left align-middle  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-left border-bottom'},
        { key: 'paymentReference', label: 'Payment Reference', thClass: 'text-left align-middle  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-left border-bottom'},
        { key: 'transactionType', label: 'Type', thClass: 'text-center align-middle', thClass:'text-nowrap  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-center font-weight-bold text-nowrap border-bottom'},
        { key: 'viban', label: 'VIBAN', thClass: 'text-right align-middle text-nowrap  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-right font-weight-bold border-bottom'},
        { key: 'amount', label: 'Amount', thClass: 'text-right align-middle text-nowrap  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-right border-bottom'},
        { key: 'type', label: 'Type', thClass: 'text-center align-middle', thClass:'text-nowrap  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-center text-nowrap border-bottom'},
        { key: 'reconciledBy', label: 'Reconciled By', thClass: 'text-center align-middle text-nowrap  border-top border-bottom bg-light border-left-0 border-right-0', tdClass: 'align-middle text-center border-bottom'},
        { key: 'status', label: 'Status', thClass: 'text-left align-middle  border-top border-bottom border-right bg-light border-left-0', tdClass: 'align-middle text-left text-nowrap border-right border-bottom'}
      ],
      stats : [
        {
          "title":"Total Reconciled",
          "description": "0",
          "description2": "0.00€",
          "color": "#00a63e",
          "background": "#dcfce7",
          "symbol": "fa-circle-check"
        },
        {
          "title":"Automation Rate",
          "description": "0.0%",
          "description2": "0 automated",
          "color": "#155dfc",
          "background": "#dbeafe",
          "symbol": "fa-chart-line-up"
        },
        {
          "title":"Manual Reconciliations",
          "description": "0",
          "description2": "Operator interventions",
          "color": "#f54a00",
          "background": "#ffedd4",
          "symbol": "fa-user-group"
        },
        {
          "title":"Report Period",
          "description": '',
          "description2": "Single day report",
          "color": "#9810fa",
          "background": "#f3e8ff",
          "symbol": "fa-clock"
        }
      ],
      paginationObj: {
        FromDate: null,
        NonReconcilie: 0
      },
      dateNow: null,
      dateBegin: new Date().toLocaleDateString(),
      dateBeginError: '',
      selectedItems: [],
      isSidebarOpen: true,
      isLoading: false,
      totalResults: null,
      totalVibanAvailable: null,
      totalVibanAssigned: null,
    }
  },
  created(){
    this.paginationObj.FromDate = new Date().toISOString().split('T')[0]
    this.submit()
  },
  methods: {
    submit(){
      this.paginationObj.firstIndex = 1;
      this.currentPage = 1;
      
      this.$engine.services.payments.getTransactions(this.paginationObj)
      .then(resp => {
        const data = resp.data
        this.totalResults = resp.data.nbTotalResults
        const transactionsReconcilied = data.Transactions.filter(t => t.ReconciliationMode == 1)
        const transactionsManReconcilied = data.Transactions.filter(t => t.ReconciliationMode == 2)
        const NbAutoReconciled = transactionsReconcilied.length
        const NbManReconciled = transactionsManReconcilied.length
        const totalReconciliation = NbAutoReconciled + NbManReconciled
        const sum = data.Transactions.filter(t => t.ReconciliationMode == 1 || t.ReconciliationMode == 2).reduce((sum, transaction) => 
          sum + parseInt(transaction.Amount)
        , 0)
        this.stats[0].description = totalReconciliation
        this.stats[0].description2 = sum.toFixed(2) + '€'
        this.stats[1].description = totalReconciliation == 0 ? '100%' : (( NbAutoReconciled * 100 ) / ( totalReconciliation )).toFixed(2) + '%'
        this.stats[1].description2 = NbAutoReconciled + ' automated'
        this.stats[2].description = NbManReconciled
        this.stats[3].description = this.formatDate(this.paginationObj.FromDate , true)
        this.items = data.Transactions
        this.dateNow = new Date().toLocaleString()
      })
      .catch(err => {
        console.log(err)
      })
    },
    toggleSelection(event, value) {
      const checked = event.target.checked;

      if (checked) {
        if (!this.selectedItems.some(item => item.IdTransaction == value.IdTransaction)) {
          this.selectedItems.push(value)
        }
      } else {
        const checkbox = document.querySelectorAll('input[type="checkbox"]')[0]
        if(checkbox.checked){
          checkbox.checked = false
        }
        this.selectedItems = this.selectedItems.filter(item => item.IdTransaction !== value.IdTransaction);
      }
    },
    selectAll(event){
      const checked = event.target.checked;
      const checkboxes = document.querySelectorAll('input[type="checkbox"]')
      if (checked) {
        checkboxes.forEach(item => item.checked = true)
        this.selectedItems = [...this.items];
      } else {
        this.selectedItems = []
        checkboxes.forEach(item => item.checked = false)
      }
    },
    clearSelection() {
      this.selectedItems = []
      const checkboxes = document.querySelectorAll('input[type="checkbox"]')
      checkboxes.forEach(item => item.checked = false)
    },
    exportCSV(filters){
      this.$engine.services.payments.getTransactions(filters)
      .then(resp => {
        let firstLine = 'Time;Order Reference;Payment Reference;Type;VIBAN;Amount;Type;Reconciled By;Status' + '\r\n'

        let line = '' 

        resp.data.Transactions.forEach(item => {
         line += item.CreationDate.split('T')[1] + ';' + ( item.Invoices[0] ? item.Invoices[0].InvoiceIdentifier : '' ) + ';' + item.Reference1 + ';' + (item.Type == 1 && item.Direction == 0? 'Payment' : item.Type == 0 && item.Direction == 1 ? 'Order' : item.Type == 11 ? 'Refund' : item.Type == 3 ? 'Credit Note' : '' ) + ';' + item.VIBAN + ';' + item.Amount + ';' + (item.ReconciliationMode == 1 ? 'Auto' : item.ReconciliationMode == 2 ? 'Manual' : '') + ';' + (item.ReconciliationMode == 1 ? 'System' : item.ReconciliationMode == 2 ? 'User' : '' ) + ';' + (Number(item.Status) == 2 ? 'Pending' : Number(item.Status) == 3 ? 'Reconciled' : Number(item.Status) == 1 ? 'Not Reconciled' : 'Active' )+ '\r\n'
        })
        
        let data = firstLine + line
        let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
        let downloadLink = document.createElement('a')
        let today = moment()
        let fileName = 'daily_report_' + today.unix() + '.csv'

        downloadLink.href = linkSource
        downloadLink.download = fileName
        downloadLink.click()
      })
      .catch(err => {
        console.log(err)
      })
    },
    exportAll () {
      this.exportCSV({NonReconcilie: 0})
    },
    exportFiltered () {
      this.exportCSV({
        firstIndex: 1,
        FromDate: this.paginationObj.FromDate
      })
    },
    exportSelected (){
      const selection = [...this.selectedItems];
      let firstLine = 'Time;Order Reference;Payment Reference;Type;VIBAN;Amount;Type;Reconciled By;Status' + '\r\n'

      let line = '' 

      selection.forEach(item => {
        line += item.CreationDate.split('T')[1] + ';' + ( item.Invoices[0] ? item.Invoices[0].InvoiceIdentifier : '' ) + ';' + item.Reference1 + ';' + (item.Type == 1 && item.Direction == 0? 'Payment' : item.Type == 0 && item.Direction == 1 ? 'Order' : item.Type == 11 ? 'Refund' : item.Type == 3 ? 'Credit Note' : '' ) + ';' + item.VIBAN + ';' + item.Amount + ';' + (item.ReconciliationMode == 1 ? 'Auto' : item.ReconciliationMode == 2 ? 'Manual' : '') + ';' + (item.ReconciliationMode == 1 ? 'System' : item.ReconciliationMode == 2 ? 'User' : '' ) + ';' + (Number(item.Status) == 2 ? 'Pending' : Number(item.Status) == 3 ? 'Reconciled' : Number(item.Status) == 1 ? 'Not Reconciled' : 'Active' )+ '\r\n'
      })
      
      let data = firstLine + line
      let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
      let downloadLink = document.createElement('a')
      let today = moment()
      let fileName = 'daily_report_' + today.unix() + '.csv'

      downloadLink.href = linkSource
      downloadLink.download = fileName
      downloadLink.click()
    },
    refresh (date = null){
      if (this.isLoading) return;
      this.isLoading = true;
      setTimeout(() => {
        this.isLoading = false;
        this.dateBegin = this.formatDate(date ?? new Date().toISOString())
        this.paginationObj = {
          firstIndex: 1,
          maxResults : 10,
          sortOrder: null,
          FromDate: date ? date.split('T')[0] : new Date().toISOString().split('T')[0]
        };
        this.submit()
      }, 800);
    },
    formatDate (date, format = null){
      const [year, month, day] = date.split('T')[0].split('-')
      if(format) return day+'.'+month+'.'+year
      return day+'/'+month+'/'+year
    },
    selectBeginDate (){
      this.dateNow = new Date().toLocaleString()
      const [day, month, year] = this.dateBegin.split('/')
      this.paginationObj.FromDate = new Date(year+'-'+month+'-'+day).toISOString()
      this.submit()
    },
  }
}
</script>
<style scoped>

.btn-validate{
  color: #ffffff;
  background-color: #00a63e;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
}

.disabled{
  opacity: 0.5;
  pointer-events: none;
  white-space: nowrap;
}

.rotate {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.selected {
  padding: 0.5rem;
  font-weight: bold;
  border-radius: calc(0.625rem - 2px);
  background-color: #dbeafe;
  color: #1d4ed8;
}

.clear{
  cursor: pointer;
  padding: 0.5rem;
  font-weight: bold;
  border-radius: calc(0.625rem - 2px);
  border: none;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: transparent;
}

.clear:hover{
  background-color: #e9ebef;
}

.fa-circle-question, .fa-chevron-down, .fa-chevron-up{
  margin: 0rem 0.5rem;
  color: #99a1af;
}

.fa-circle-question:hover{
  color: #4a5565;
}

.pagination{
  display: flex;
  align-items: center;
  color: #000000a6;
  padding: 1rem 2rem;
}

.expanded {
  padding-left: 60px;
  transition: padding-left 0.3s ease;
}

.inactive{
  color: #0a0a0a;
  opacity: .5;;
  pointer-events: none;
  outline-style: none;
}


.content-title{
  margin: 1.5rem 0rem;
}


.content-title>div>span{
  color: #000000a6;
  font-size: 1.1rem;
}

.filters{
  margin: 2rem 0rem;
}

.filters-container{
  width: 100%;
  margin: 2rem 0rem 0rem 0rem;
  border: 1px solid #0000001a;
  border-radius: calc(1rem - 2px);
  background-color: #fff;
  padding: 2rem;
}

.filters-items{
  display: grid;
  grid-template-columns: 7fr 4fr 1fr;
  padding: 0rem 1rem;
}

.filters-items>div:nth-child(2){
  display: flex;
  gap: 1rem;
}

.stats{
  display: flex;
  gap: 1rem;
  padding: 1rem 0rem;
}

.stats>div{
  display: flex;
  justify-content: center;
  align-items: center;
  height: 8rem;
  background-color: #fff;
  border: 1px solid #0000001a;
  border-radius: calc(1rem - 2px);
  padding: 0rem 1rem;
}

.stats>div>div{
  display: grid;
  row-gap: 1.5rem;
  color: #000000a6;
}

.stats>div>div>span:last-of-type{
font-size: 1rem;
}

.stats>div>div>span{
  font-size: 2rem;
  display: flex;
  justify-content: center;
}
</style>